import React, { useMemo } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import { DBO_V } from '@op/utils/format';
import { getPercentageInfo } from '@op/utils';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import { formatTimeToShow } from '@op/utils/time';
import { detailPageTips } from '@op/pages/Overview/KeyMetrics/utils';
import { METRIC_OPTIONS } from '../TagAnalysisCard';

// Constants
const CHART_CONSTANTS = {
  PIE_LABEL_COLOR: '#262832',
  EDGE_DISTANCE: 10,
  LINE_HEIGHT: 20,
  HR_LINE_WIDTH: '105%',
  HR_LINE_BORDER_WIDTH: 0.5,
  HR_LINE_HEIGHT: 0.5,
  LEGEND_OPACITY: 0.2,
  TOOLTIP_BORDER_RADIUS: 6,
};

interface IPieChartProps {
  data: any;
  filter: any;
  height?: number;
  metric: string;
  translateTags: (key: string) => string;
  formatMessage: (obj: { id: string; } | string) => string;
  eChartsRef?: React.RefObject<any>;
  extraProps?: Record<string, any>;
  defaultSelected?: any;
}

const PieChart: React.FC<IPieChartProps> = ({
  data,
  filter,
  height = 350,
  metric,
  translateTags,
  formatMessage,
  eChartsRef,
  defaultSelected,
  extraProps,
}) => {
  const options = useMemo(() => {
    const { pieData, pieTotalData, legendData } = data || {};

    return {
      series: {
        data:
          pieData?.map((val: any) => ({
            name: val.name,
            value: val.value,
            itemStyle: val.itemStyle,
          })) || [],
        type: 'pie',
        avoidLabelOverlap: true,
        center: ['50%', '45%'],
        label: {
          color: CHART_CONSTANTS.PIE_LABEL_COLOR,
          alignTo: 'labelLine',
          formatter: (params: any) => {
            const percent = getPercentageInfo(params?.value, pieTotalData, 2);
            const name = translateTags(params.name);

            let percentText = DBO_V.reviews(params.value);
            if (params?.value) {
              percentText += `(${percent})`;
            }

            return `{value| ${name}}: {a|${percentText}}`;
          },
          edgeDistance: CHART_CONSTANTS.EDGE_DISTANCE,
          rich: {
            a: {
              lineHeight: CHART_CONSTANTS.LINE_HEIGHT,
              align: 'center',
            },
            hrLine: {
              borderColor: 'auto',
              width: CHART_CONSTANTS.HR_LINE_WIDTH,
              borderWidth: CHART_CONSTANTS.HR_LINE_BORDER_WIDTH,
              height: CHART_CONSTANTS.HR_LINE_HEIGHT,
            },
            per: {
              padding: [4, 0],
            },
          },
        },
        emphasis: {
          focus: 'self',
          label: {
            opacity: CHART_CONSTANTS.LEGEND_OPACITY,
          },
          labelLine: {
            lineStyle: {
              opacity: CHART_CONSTANTS.LEGEND_OPACITY,
            },
          },
        },
        blur: {
          label: { opacity: CHART_CONSTANTS.LEGEND_OPACITY },
          itemStyle: {
            opacity: CHART_CONSTANTS.LEGEND_OPACITY,
          },
          labelLine: {
            lineStyle: {
              opacity: CHART_CONSTANTS.LEGEND_OPACITY,
            },
          },
        },
      },
      legend: {
        data: legendData,
        formatter: (params: any) => {
          return translateTags(params);
        },
        bottom: 0,
        ...(defaultSelected ? { selected: defaultSelected } : {}),
      },
      tooltip: {
        borderWidth: 0,
        borderRadius: CHART_CONSTANTS.TOOLTIP_BORDER_RADIUS,
        padding: 0,
        formatter: (params: any) =>
          formatChartParamsDate({
            className: 'dbo-tag-analysis-chart-tooltip',
            params,
            listTitleFunc: (item: any) => {
              return translateTags(item.name);
            },
            tablesConfigFunc: (records: any) => {
              const valueText = METRIC_OPTIONS.find(option => option.value === metric)?.text || 'Value';
              const columns = [
                {
                  key: 'text',
                  header: formatTimeToShow(filter),
                },
                {
                  key: 'value',
                  align: 'right',
                  weight: 'bold',
                  header: formatMessage(valueText),
                },
              ];

              detailPageTips(records, formatMessage, extraProps?.detailPage);

              return [
                {
                  showHeader: true,
                  records,
                  columns,
                  className: 'dbo-tag-analysis-chart-tooltip-table'
                },
              ];
            },
            valueFormat: (value: number) => {
              const percent = getPercentageInfo(value, pieTotalData, 2);
              return `${isNaN(value) ? '-' : DBO_V.reviews(value)}(${percent})`;
            },
          }),
      },
      grid: {
        bottom: 30,
      }
    };
  }, [data, filter, metric, translateTags, formatMessage]);

  return (
    <EChartCommon
      optionsProps={{ type: 'pie', excludeOptions: ['grid'] }}
      height={height}
      options={options}
      ref={eChartsRef}
      {...extraProps}
    />
  );
};

export default PieChart;
